import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";
import Pagination from "@mui/material/Pagination";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiSelect from "components/SuiSelect";
import SuiTypography from "components/SuiTypography";
import ConnectionCard from "pages/connect/components/ConnectionCard";
import ProfileCard from "pages/connect/components/ProfileCard";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import api from "services/api";
import { maCatOptions, usaStates } from "utils/data";

const usaStateOptions = usaStates.map((x) => {
  return {
    label: x.abbreviation,
    value: x.abbreviation,
  };
});

const ShowList = ({ list, filterData }) => {
  const [dataList, setDataList] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  useEffect(() => {
    setDataList(list);
  }, [list, filterData]);

  const pageCount = parseInt(Math.ceil(dataList.length / pageSize), 10);
  //console.log('pageCount', pageCount, 'pageSize', pageSize)
  const entriesStart = pageSize * (page - 1) + 1;
  const entriesEnd = Math.min(dataList.length, pageSize * page);
  const pageOptions = [...Array(pageCount).keys()];

  const handleChangeRowsPerPage = (option) => {
    const newPageSize = parseInt(option.value, 10);
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <SuiBox sx={{ mt: 2 }}>
      <Grid container spacing={3} alignItems="stretch">
        {dataList.slice(entriesStart - 1, entriesEnd).map((u) => {
          return (
            <Grid key={u.id} item xs={12} sm={12} md={6} lg={4} xl={3}>
              <ProfileCard profile={u} />
            </Grid>
          );
        })}
      </Grid>
      <SuiBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={3}
      >
        <div className="flex items-center">
          <SuiBox mb={{ xs: 3, sm: 0 }}>
            <SuiTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {dataList.length} entries
            </SuiTypography>
          </SuiBox>

          <SuiBox display="flex" alignItems="center" gap={1}>
            <SuiSelect
              defaultValue={{ value: pageSize, label: pageSize }}
              options={[4, 8, 16].map((entry) => ({ value: entry, label: entry }))}
              onChange={handleChangeRowsPerPage}
              size="small"
            />
            <SuiTypography variant="caption" color="secondary">
              entries per page
            </SuiTypography>
          </SuiBox>
        </div>
        <Pagination
          variant={"outlined"}
          color={"info"}
          count={pageOptions.length}
          defaultPage={page}
          onChange={handleChangePage}
        />
      </SuiBox>
    </SuiBox>
  );
};

const ConnectPage = ({ type = "de" }) => {
  const [recommendedList, setRecommendedList] = useState([]);
  const [trendingmaList, setTrendingmaList] = useState([]);
  const [filterData, setFilterData] = useState({});

  const location = useLocation();
  const url = location.href ? location.href : location.pathname + location.search;

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
    control,
  } = useForm();

  useEffect(() => {
    retrieveList({ occupation: { label: type, value: type } });
  }, [url]);

  const handleApply = async (data) => {
    //console.log('data', data)
    data["occupation"] = { label: type, value: type };
    setFilterData(data);
    retrieveList(data);
  };

  const retrieveList = async (payload) => {
    const { data } = await api.social.recommendedlist(payload);
    setRecommendedList(data.list.recommendedlist);
    setTrendingmaList(data.list.trendingmalist);
  };

  return (
    <SuiBox sx={{ p: 3 }}>
      <SuiBox component="form" pb={1} onSubmit={handleSubmit(handleApply)}>
        <SuiBox display="flex" alignItems="center" sx={{ gap: 1, flexWrap: "wrap" }}>
          <SuiTypography variant="button" color="dark" fontWeight="regular">
            Filter by:
          </SuiTypography>

          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <Card sx={{ borderRadius: 2 }}>
                <SuiSelect
                  {...field}
                  options={[
                    { label: "Design", value: "Design" },
                    { label: "Development", value: "Development" },
                    { label: "Sourcing", value: "Sourcing" },
                    { label: "Production", value: "Production" },
                  ]}
                  placeholder="Product Category"
                  isClearable
                  onChange={(newValue, actionMeta) => {
                    let payload = {};
                    payload[field.name] = newValue;
                    //handleApply(payload);
                    field.onChange(newValue);
                  }}
                />
              </Card>
            )}
          />
          <Controller
            name="service"
            control={control}
            render={({ field }) => (
              <Card sx={{ borderRadius: 2 }}>
                <SuiSelect
                  {...field}
                  options={maCatOptions}
                  placeholder="Services"
                  isClearable
                  onChange={(newValue, actionMeta) => {
                    let payload = {};
                    payload[field.name] = newValue;
                    //handleApply(payload);
                    field.onChange(newValue);
                  }}
                />
              </Card>
            )}
          />
          <Controller
            name="province"
            control={control}
            render={({ field }) => (
              <Card sx={{ borderRadius: 2 }}>
                <SuiSelect
                  {...field}
                  options={usaStateOptions}
                  placeholder="Province/State"
                  backspaceRemovesValue
                  isClearable
                  onChange={(newValue, actionMeta) => {
                    let payload = {};
                    payload[field.name] = newValue;
                    //handleApply(payload);
                    field.onChange(newValue);
                  }}
                />{" "}
              </Card>
            )}
          />
          <div>
            <SuiInput placeholder="Keywords" type="text" {...register("keywords")} />
          </div>
          <div>
            <SuiButton variant="gradient" color="dark" sx={{ height: "100%" }} type="submit">
              Apply
            </SuiButton>
          </div>
        </SuiBox>
      </SuiBox>
      <SuiBox display="flex" gap={1}>
        <SuiBox sx={{ flexGrow: 0, minWidth: 260, width: 260, height: "100%" }}>
          <ConnectionCard />
        </SuiBox>
        <SuiBox sx={{ flexGrow: 1 }}>
          <SuiBox>
            <SuiTypography variant="h6" fontWeight="bold" textTransform="none">
              Recommended Connections
            </SuiTypography>
            <ShowList list={recommendedList} filterData={filterData} />
          </SuiBox>
          {type == "ma" ? (
            <SuiBox sx={{ mt: 2 }}>
              <SuiTypography variant="h6" fontWeight="bold" textTransform="none">
                Trending Manufacturers
              </SuiTypography>
              <ShowList list={trendingmaList} filterData={filterData} />
            </SuiBox>
          ) : null}
        </SuiBox>
      </SuiBox>
    </SuiBox>
  );
};

export default ConnectPage;
